import { useState } from "react";
import { toast } from "sonner";
import { Seo, Eyebrow } from "@/components/site/primitives";
import { Reveal } from "@/components/site/motion";
import { PageHero, Section } from "@/components/site/pageparts";
import { TextField, TextArea, SelectField, SubmitButton } from "@/components/site/forms";
import { HumanCheck } from "@/components/site/HumanCheck";
import { api } from "@/lib/api";
import { COMPANY } from "@/data/site";

const INTENTS = [
  "Research collaboration",
  "Enterprise (UEII / Membrane pilot)",
  "Forward Deployed Engineering",
  "Press / media",
  "Careers",
  "General",
];

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [human, setHuman] = useState({ valid: false, honeypot: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!human.valid) {
      toast.error("Please answer the spam check to continue.");
      return;
    }
    const f = e.target;
    setLoading(true);
    try {
      await api.post("/contact", {
        name: f.name.value,
        email: f.email.value,
        organization: f.organization.value,
        intent: f.intent.value,
        message: f.message.value,
        company_website: human.honeypot,
      });
      toast.success("Thank you — we'll be in touch shortly.");
      f.reset();
    } catch (err) {
      const s = err?.response?.status;
      const msg =
        s === 429
          ? "Too many messages from this network. Please try again shortly."
          : s === 502
          ? "Our email provider is momentarily unavailable — please retry in a minute, or email hello@datafrontier.co."
          : "Something went wrong. Please email hello@datafrontier.co.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <Seo title="Contact" description="Talk to DataFrontier — research collaboration, enterprise pilots, press, careers." />
      <PageHero
        eyebrow="Company · Contact"
        title="Talk to us."
        lead="Tell us what you're working on. We split enquiries by intent so the right person replies."
      />

      <Section>
        <div className="grid gap-16 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <Eyebrow>By intent</Eyebrow>
            <ul className="mt-8 space-y-6">
              {[
                { t: "Research collaboration", d: "Retrieval, evaluation, memory — let's compare notes." },
                { t: "Enterprise", d: "UEII and Smritium Membrane pilots. On-prem and air-gapped welcome." },
                { t: "Press", d: COMPANY.emails.press },
                { t: "Careers", d: COMPANY.emails.careers },
              ].map((i) => (
                <li key={i.t} className="border-l-2 border-white/10 pl-5">
                  <div className="font-serif text-lg text-bone">{i.t}</div>
                  <div className="mt-1 text-sm text-mist">{i.d}</div>
                </li>
              ))}
            </ul>

            <div className="mt-12 border border-white/10 bg-white/[0.015] p-6">
              <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-mist">Registered office</div>
              <div className="mt-3 space-y-1 text-sm text-mist">
                <div className="text-bone">{COMPANY.legalName}</div>
                <div>{COMPANY.address}</div>
                <div className="font-mono">CIN {COMPANY.cin}</div>
              </div>
            </div>

            <p className="mt-8 text-sm leading-relaxed text-mist">
              For UPII open-source issues, please use{" "}
              <a href="https://pypi.org/project/upii/" target="_blank" rel="noreferrer" className="link-underline pb-0.5 text-bone">
                GitHub / PyPI
              </a>{" "}
              rather than this form.
            </p>
          </Reveal>

          <Reveal delay={0.1} className="lg:col-span-7">
            <form onSubmit={submit} className="grid gap-6 sm:grid-cols-2" data-testid="contact-form">
              <TextField id="name" label="Your name" name="name" required placeholder="Ada Lovelace" />
              <TextField id="email" label="Work email" name="email" type="email" required placeholder="you@company.com" />
              <TextField id="organization" label="Organization" name="organization" placeholder="Company / institution" />
              <SelectField id="intent" label="I'm here to" name="intent" options={INTENTS} />
              <div className="sm:col-span-2">
                <TextArea id="message" label="Message" name="message" required rows={6} placeholder="What are you trying to solve…" />
              </div>
              <div className="sm:col-span-2">
                <HumanCheck onChange={setHuman} />
              </div>
              <div className="sm:col-span-2">
                <SubmitButton loading={loading} disabled={!human.valid}>Send message</SubmitButton>
              </div>
            </form>
          </Reveal>
        </div>
      </Section>
    </div>
  );
}
